import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Users } from "lucide-react";
import { toast } from "sonner";

export default function CheckerPools() {
  const [pools, setPools] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [users, setUsers] = useState("");

  const fetchData = async () => {
    try {
      const [poolsRes, policyRes] = await Promise.all([api.get("/admin/checker-pools"), api.get("/admin/maker-checker/policies")]);
      setPools(poolsRes.data.pools || []);
      setPolicies(policyRes.data.policies || []);
    } catch {
      toast.error("Failed to load checker pools");
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const createPool = async () => {
    try {
      await api.post("/admin/checker-pools", { name, assigned_user_ids: users.split(",").map((item) => item.trim()).filter(Boolean) });
      toast.success("Checker pool created");
      setName("");
      setUsers("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create checker pool");
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10 space-y-6" data-testid="checker-pools-page">
      <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3"><Users className="w-8 h-8 text-court-orange" />Checker Pools</h1>
      <Card>
        <CardHeader><CardTitle className="font-heading text-lg">Create Checker Pool</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div><Label>Pool Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="checker-pool-name" /></div>
          <div><Label>Assigned User IDs (comma-separated)</Label><Input value={users} onChange={(e) => setUsers(e.target.value)} data-testid="checker-pool-users" /></div>
          <Button onClick={createPool} data-testid="create-checker-pool-btn">Create Pool</Button>
        </CardContent>
      </Card>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="font-heading text-lg">Existing Pools</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {pools.map((pool) => <div key={pool.pool_id} className="rounded-lg border border-zinc-200 p-3" data-testid={`checker-pool-${pool.pool_id}`}>{pool.name} · {(pool.assigned_user_ids || []).length} users</div>)}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="font-heading text-lg">Approval Policies</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {policies.map((policy) => <div key={policy.action_key} className="rounded-lg border border-zinc-200 p-3" data-testid={`maker-checker-policy-${policy.action_key}`}>{policy.label} · {policy.approvals_required} approval(s)</div>)}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}